import { CoreOptions as AdyenOptions } from '@adyen/adyen-web/dist/types/core/types';
import CustomAmountInput from './CustomAmountInput';
import serialize from '../utils/form/serialize';
import ResponseHandlerRetriever from './hostedPayment/responseHandlers/ResponseHandlerRetriever';
import Messaging from './OnePageGivingMessaging';
import OnePageGivingComponent from './components/OnePageGivingComponent';
import dom from '../utils/dom';
import Field from './OnePageGivingFormField';
import OnePageGivingButtons from './OnePageGivingButtons';
import FormHandler, { FormHandlerSubmitCallback } from '../forms/FormHandler';
import type CurrencyFormatter from '../tnew/currency/CurrencyFormatter';
import type SuggestedAmountButton from './SuggestedAmountButton';
import type {
  ResponseHandlerObject, ResponseHandlerResult, SubmitResponse, AdditionalActionHandlerResult,
} from './types';
import StateFilteredAddress from '../forms/address/StateFilteredAddress';
import { Country, State } from '../forms/address/types';
import PaymentProviderSubmitResult from '../payment/PaymentProviderSubmitResult';
import disableForm from '../utils/form/disableForm';
import Service from './OnePageGivingService';
import PassThroughFormPostHandler from '../forms/PassThroughFormPostHandler';
import IPaymentProvider from '../payment/IPaymentProvider';
import { IRecaptchaSettings, RecaptchaService } from '../utils/RecaptchaService';
import { IViewItemAnalytics } from '../analytics/types';
import fireAddToCartAnalytics from '../analytics/fireAddToCartAnalytics';

export interface StateFilterConfig {
  states: State[];
  countries: Country[];
}

function isAdditionalActionResult(handlerResult: ResponseHandlerResult): handlerResult is AdditionalActionHandlerResult {
  return (handlerResult as AdditionalActionHandlerResult).paymentProviderData !== undefined;
}

class OnePageGivingForm {
  $el: HTMLFormElement;

  currencyFormatter: CurrencyFormatter;

  recaptchaService: RecaptchaService;

  $totalPending: HTMLSpanElement;

  $totalValid: HTMLSpanElement;

  suggestedAmountButtons: SuggestedAmountButton[];

  currentButton: SuggestedAmountButton | undefined;

  customAmountInput: CustomAmountInput;

  originalAmount: number | undefined;

  amount: number | undefined;

  responseHandlerRetriever: ResponseHandlerRetriever;

  $paymentTarget: HTMLElement | null = null;

  isSubmitting = false;

  formHandler: FormHandler;

  paymentConfiguration: AdyenOptions | null;

  reenableForm: Function | undefined;

  stopSubmitting: Function | undefined;

  private viewItemAnalytics?: IViewItemAnalytics;

  constructor($form: HTMLFormElement,
    currencyFormatter: CurrencyFormatter,
    recaptchaService: RecaptchaService,
    private recaptchaSettings: IRecaptchaSettings,
    paymentConfiguration: AdyenOptions | null,
    stateFilterConfig?: StateFilterConfig,
    private readonly paymentProvider?: IPaymentProvider | undefined,
    viewItemAnalytics?: IViewItemAnalytics) {
    this.viewItemAnalytics = viewItemAnalytics;
    this.$el = $form;
    this.currencyFormatter = currencyFormatter;
    this.recaptchaService = recaptchaService;
    this.paymentConfiguration = paymentConfiguration;

    this.$totalPending = dom.findFrom($form, '.tn-opg-form__total-amount--pending') as HTMLSpanElement;
    this.$totalValid = dom.findFrom($form, '.tn-opg-form__total-amount--valid') as HTMLSpanElement;

    if (paymentConfiguration) {
      this.$paymentTarget = dom.find('#tn-payment-drop-target') as HTMLElement;

      if (this.$paymentTarget) {
        this.paymentProvider?.setup(this.$paymentTarget);
      }
    }

    this.formHandler = new FormHandler(this.$el, {
      postHandler: PassThroughFormPostHandler,
      onValidationSuccess: this.onValidation,
      onValidationAjaxException: this.onSubmitAjaxException,
      formDataRetriever: this,
    });

    if (stateFilterConfig) {
      new StateFilteredAddress(this.formHandler, {
        countries: stateFilterConfig.countries,
        states: stateFilterConfig.states,
        fields: {
          Street1: 'GivingPageBillingAddress.Address1',
          Street2: 'GivingPageBillingAddress.Address2',
          CountryId: 'GivingPageBillingAddress.CountryId',
          StateId: 'GivingPageBillingAddress.StateId',
          PostalCode: 'GivingPageBillingAddress.PostalCode',
          City: 'GivingPageBillingAddress.City',
        },
      });
    }

    this.responseHandlerRetriever = new ResponseHandlerRetriever(this.paymentProvider, this.formHandler);

    this.customAmountInput = new CustomAmountInput(this.$el, this.onCustomInputUpdate);

    const buttons = new OnePageGivingButtons(this.$el, this.onButtonClick);

    this.suggestedAmountButtons = buttons.suggestedAmountButtons;
    this.currentButton = buttons.currentButton;
  }

  onButtonClick = (btn: SuggestedAmountButton) => {
    const { currentButton } = this;

    if (btn === currentButton) {
      return;
    }

    if (btn.isOtherAmountButton) {
      this.customAmountInput.expand();
    } else {
      this.customAmountInput.collapse();
    }

    this.currentButton = btn;

    if (btn.isOtherAmountButton) {
      this.updateTotalForCustomInput();
    } else {
      this.setAmount(btn.value);
      this.displayTotal();
    }
  };

  setAmount(newAmount: number) {
    this.originalAmount = this.amount;
    this.amount = newAmount;

    this.updateTotalDisplay();
    this.displayTotal();

    if (this.originalAmount !== newAmount) {
      this.paymentProvider?.reload(this.$paymentTarget, newAmount);
    }
  }

  updateTotalDisplay() {
    const { amount } = this;
    const formatted = this.currencyFormatter.format(amount!);

    this.$totalValid.textContent = formatted;
  }

  displayPending() {
    dom.toggle(this.$totalPending, true);
    dom.toggle(this.$totalValid, false);
  }

  displayTotal() {
    dom.toggle(this.$totalPending, false);
    dom.toggle(this.$totalValid, true);
  }

  onCustomInputUpdate = () => {
    if (this.currentButton && !this.currentButton.isOtherAmountButton) { // TODO track mode not buttons
      return;
    }

    this.updateTotalForCustomInput();
  };

  updateTotalForCustomInput() {
    const { isAmountValid, value } = this.customAmountInput;

    if (isAmountValid) {
      this.setAmount(value);

      this.displayTotal();
    } else {
      this.displayPending();
    }
  }

  getAmountData() {
    const {
      suggestedAmountButtons,
      customAmountInput,
      currentButton,
    } = this;

    const customAmountResult = {
      amount: customAmountInput.rawValue,
      isAmountCustom: true,
    };

    if (!suggestedAmountButtons.length) {
      return customAmountResult;
    }

    if (!currentButton) {
      return { amount: 0, isAmountCustom: false };
    }

    if (currentButton && currentButton.isOtherAmountButton) {
      return customAmountResult;
    }

    return {
      amount: currentButton.value,
      isAmountCustom: false,
    };
  }

  recaptchaToken: string = '';

  getFormData() {
    const formData = serialize(this.$el);
    const { amount, isAmountCustom } = this.getAmountData();

    return ({
      ...formData,
      [Field.IsAmountCustom]: isAmountCustom,
      [Field.Amount]: amount,
      [Field.HasButtonSelected]: !!this.currentButton?.id,
      [Field.RecaptchaResponse]: this.recaptchaToken,
      [Field.IsEmbeddedHostedPayment]: !!this.paymentProvider,
    });
  }

  onAuthorizeFailed(responseBody: any): void {
    this.isSubmitting = false;
    this.recaptchaService.reset();

    if (responseBody?.validationResult?.length) {
      this.formHandler.processValidationResults(responseBody.validationResult);
      this.reenableEverything();
      return;
    }

    Messaging.addPageError(responseBody.error || 'Authorization failed. Please try again.');

    this.reenableEverything();
  }

  reenableEverything = () => {
    this.isSubmitting = false;
    this.stopSubmitting();

    this.paymentProvider?.enable();

    window.setTimeout(() => {
      this.reenableForm?.();
    });
  };

  onValidation: FormHandlerSubmitCallback = async ({ stopSubmitting }) => {
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    this.stopSubmitting = stopSubmitting;

    this.formHandler.controlGroups.forEach((group) => group.clearErrors());

    Messaging.clearErrors();

    if (this.recaptchaSettings.isRecaptchaEnabled) {
      try {
        const token = await this.recaptchaService.challenge('signup');
        if (token === undefined) {
          this.isSubmitting = false;
          stopSubmitting();
          return;
        }
        this.recaptchaToken = token;
      } catch (err) {
        this.isSubmitting = false;
        stopSubmitting();
        return;
      }
    }

    let formData = this.getFormData();

    this.reenableForm = disableForm(this.$el).reenableForm;

    let paymentProviderData: PaymentProviderSubmitResult | null = null;

    if (this.paymentProvider) {
      paymentProviderData = await this.paymentProvider.submit(formData);

      this.paymentProvider.disable();

      if (paymentProviderData.isCancelled || paymentProviderData?.isSubmitted) {
        stopSubmitting();

        this.reenableEverything();

        return;
      }

      if (this.paymentProvider?.getType() === 'MerchantServicesPaymentProvider') {
        return;
      }
    }

    let response: SubmitResponse;
    let responseHandler: ResponseHandlerObject;
    let hasTrackedAddToCart = false;

    try {
      let handlerResult: ResponseHandlerResult;
      do {
        if (paymentProviderData) {
          const { error, paymentMethodData, additionalData } = paymentProviderData;

          if (error) {
            window.location.href = '/donate/q/hpr/clientreloadfromerror';
          }

          formData = {
            ...formData,
            ...{
              [Field.EmbeddedPaymentPaymentMethodData]: paymentMethodData,
              [Field.EmbeddedPaymentAdditionalData]: additionalData,
            },
          };
        }

        // eslint-disable-next-line no-await-in-loop
        response = await Service.submit(formData);

        if (!hasTrackedAddToCart) {
          fireAddToCartAnalytics(this.viewItemAnalytics, [{ price: this.amount || 0, quantity: 1, itemVariant: '' }]);
          hasTrackedAddToCart = true;
        }

        responseHandler = this.responseHandlerRetriever.get(response);

        // eslint-disable-next-line no-await-in-loop
        handlerResult = await responseHandler.handle(response, this.recaptchaService);

        if (handlerResult && isAdditionalActionResult(handlerResult) && handlerResult.paymentProviderData) {
          paymentProviderData = handlerResult.paymentProviderData;
        }
      } while (handlerResult && isAdditionalActionResult(handlerResult) && handlerResult.shouldResubmit);
    } catch (e) {
      this.recaptchaService.reset();

      window.location.href = '/donate/q/hpr/clientreloadfromerror';
    }

    this.paymentProvider?.showValidation();

    this.reenableEverything();
  };

  onSubmitAjaxException = () => {
    Messaging.addPageError(OnePageGivingComponent.errorMessage);

    this.recaptchaService.reset();
  };
}

export default OnePageGivingForm;
